/**
 * **Einen Mailtext in Nachricht, Signatur und Verlauf zerlegen**
 * ([E-384](../../../docs/ENTSCHEIDUNGEN.md#e-384), 01.10.2026).
 *
 * Christian Hunhoff, 30.09.2026, zu den Kommentaren: *„hier müsste an der Lesbarkeit von
 * Einträgen bzw. Kommentaren gearbeitet werden. Denn es handelt sich hier um einen einzigen
 * Textblock"* – und im zweiten Ticket: *„je nach Menge des Texts und wenn es auch noch eine
 * Mail ist, sind die Kommentar echt unübersichtlich zu lesen."*
 *
 * ## Was gemessen wurde, bevor etwas gebaut wurde
 *
 * Der naheliegende Verdacht war das HTML-Entfernen. Gemessen am Bestand (01.10.2026) stimmt er
 * nicht: Von **2.065** Beschreibungen mit echtem HTML bleibt in **einer** ein Tag stehen, bei
 * den Kommentaren in keiner. `htmlZuText` arbeitet sauber.
 *
 * Gemessen wurde stattdessen, **woraus die Texte bestehen**. An einer echten Aufgabe
 * (`01M3RNRH…`, „WG: FRITZBOX bei Upgrade"): 8.223 Zeichen HTML werden 1.721 Zeichen Text in 26
 * Absätzen – davon sind **drei Zeilen die Nachricht**. Der Rest ist Signatur,
 * Haftungsausschluss auf Deutsch und Englisch und die zitierte Vormail. Alles in derselben
 * Größe, alles gleich wichtig.
 *
 * Das ist der „einzige Textblock": nicht zu viel Text, sondern **keine Abstufung**.
 *
 * ## Die Leitplanke: lieber nichts einklappen als das Falsche
 *
 * Jede Erkennung hier ist eine Heuristik, und eine Heuristik, die eine Nachricht versteckt, ist
 * schlimmer als gar keine – der Leser sucht dann einen Text, von dem er nicht weiß, dass es ihn
 * gibt. Deshalb zwei harte Regeln:
 *
 * 1. **Bliebe oben nichts stehen, wird gar nicht geteilt.** Eine Mail, die mit „Mit
 *    freundlichen Grüßen" anfängt, ist dann eben ein Block – so wie bisher.
 * 2. **Nichts wird weggeworfen.** Die Abschnitte ergeben wieder den ganzen Text; die Oberfläche
 *    klappt sie nur zu.
 */

/** Ein Stück Mailtext und was es ist. */
export type Mailabschnitt = {
  art: 'inhalt' | 'signatur' | 'verlauf'
  text: string
  /** Zeilen mit Inhalt – für die Beschriftung am Aufklapper („… 12 Zeilen"). */
  zeilen: number
}

/**
 * **Der Kopf einer zitierten Mail.** Outlook schreibt ihn als Block aus vier Zeilen; eine
 * einzelne reicht **nicht** als Beleg, sonst risse ein „Datum: siehe Anlage" mitten im Satz den
 * Text auseinander. Deshalb verlangt {@link verlaufAb} zwei davon dicht beieinander.
 */
const KOPFZEILE =
  /^\s*(Von|From|Gesendet|Sent|Datum|Date|An|To|Cc|CC|Betreff|Subject|Absender)\s*:\s*\S/

/** Die Trennlinie, die Outlook und Thunderbird vor eine zitierte Mail setzen. */
const TRENNER =
  /^\s*-{2,}\s*(Urspr[üu]ngliche Nachricht|Original Message|Weitergeleitete Nachricht|Forwarded [Mm]essage)/i

/** Die Gmail-Zeile: „Am 30.09.2026 schrieb Marion Deckeling:" */
const AM_SCHRIEB = /^\s*Am\s.{4,70}\sschrieb\s.{1,90}:\s*$/i

/** Eine zitierte Zeile. Zwei hintereinander sind ein Verlauf, eine einzelne ist Zufall. */
const ZITAT = /^\s*>/

/**
 * **Die Grußformel, mit der eine Signatur anfängt.**
 *
 * Die Zeile muss **kurz** sein: „Mit freundlichen Grüßen" steht allein, während *„Viele Grüße
 * gehen auch an die Kollegen in Vreden, die das vorbereitet haben"* ein Satz ist und bleiben
 * muss.
 */
const GRUSS =
  /^\s*(Mit freundlichen Gr[üu][ßs]en|Mit freundlichem Gru[ßs]|Viele Gr[üu][ßs]e|Beste Gr[üu][ßs]e|Freundliche Gr[üu][ßs]e|Liebe Gr[üu][ßs]e|Herzliche Gr[üu][ßs]e|Sch[öo]ne Gr[üu][ßs]e|Gr[üu][ßs]e|Best regards|Kind regards|Mit besten Gr[üu][ßs]en|MfG|VG)\b[\s,.!]*$/i

/** Höchstlänge einer Zeile, die noch als Grußformel durchgeht. */
const GRUSS_MAX = 40

/**
 * Wo der zitierte Verlauf anfängt – oder `-1`.
 *
 * Gesucht wird die **früheste** der vier Formen. Zwei Kopfzeilen müssen innerhalb von sechs
 * Zeilen beieinanderstehen: Outlook schreibt „Von / Gesendet / An / Betreff" am Stück, und
 * dazwischen stehen manchmal Leerzeilen.
 */
function verlaufAb(zeilen: readonly string[]): number {
  for (let i = 0; i < zeilen.length; i++) {
    const z = zeilen[i] ?? ''
    if (TRENNER.test(z) || AM_SCHRIEB.test(z)) return i
    if (ZITAT.test(z) && ZITAT.test(zeilen[i + 1] ?? '')) return i
    if (KOPFZEILE.test(z)) {
      const weitere = zeilen.slice(i + 1, i + 7).filter((w) => KOPFZEILE.test(w)).length
      if (weitere >= 1) return i
    }
  }
  return -1
}

/** Wo die Signatur anfängt – oder `-1`. Nur **vor** dem Verlauf gesucht. */
function signaturAb(zeilen: readonly string[]): number {
  for (let i = 0; i < zeilen.length; i++) {
    const z = zeilen[i] ?? ''
    if (z.trim().length <= GRUSS_MAX && GRUSS.test(z)) return i
  }
  return -1
}

/** Hat dieser Ausschnitt überhaupt etwas zu sagen? */
function hatInhalt(zeilen: readonly string[]): boolean {
  return zeilen.some((z) => z.trim() !== '')
}

function abschnitt(art: Mailabschnitt['art'], zeilen: readonly string[]): Mailabschnitt {
  return {
    art,
    text: zeilen.join('\n').replace(/^\n+/, '').replace(/\n+$/, ''),
    zeilen: zeilen.filter((z) => z.trim() !== '').length,
  }
}

/**
 * Den Text in bis zu drei Abschnitte zerlegen – immer in der Reihenfolge Inhalt, Signatur,
 * Verlauf. Abschnitte ohne Inhalt entstehen gar nicht erst.
 *
 * Gibt es nichts zu trennen, kommt **ein** Abschnitt `inhalt` zurück: Der Aufrufer braucht
 * keinen Sonderweg für den gewöhnlichen Fall.
 */
export function mailAufteilen(text: string): Mailabschnitt[] {
  const zeilen = text.split('\n')
  const vAb = verlaufAb(zeilen)
  const vorVerlauf = vAb === -1 ? zeilen : zeilen.slice(0, vAb)
  const sAb = signaturAb(vorVerlauf)

  const inhalt = sAb === -1 ? vorVerlauf : vorVerlauf.slice(0, sAb)

  /**
   * **Die Leitplanke.** Bliebe oben nichts stehen, hätte das Einklappen den ganzen Text
   * versteckt – und der Leser suchte etwas, von dem er nicht weiß, dass es da ist.
   */
  if (!hatInhalt(inhalt)) return [abschnitt('inhalt', zeilen)]

  const abschnitte: Mailabschnitt[] = [abschnitt('inhalt', inhalt)]
  const signatur = sAb === -1 ? [] : vorVerlauf.slice(sAb)
  if (hatInhalt(signatur)) abschnitte.push(abschnitt('signatur', signatur))
  const verlauf = vAb === -1 ? [] : zeilen.slice(vAb)
  if (hatInhalt(verlauf)) abschnitte.push(abschnitt('verlauf', verlauf))
  return abschnitte
}
